import axios, { AxiosInstance } from "axios";
import map from "lodash/map";
import CheerzApiAddresses from "@Types/api/addresses";
import { CheerzApiBannerKountries } from "@Types/api/bannerKountries";
import CheerzApiCart from "@Types/api/cart";
import { CheerzApiCreditCard } from "@Types/api/creditCard";
import { CheerzApiArticle, CheerzApiCustomization } from "@Types/api/customization";
import { CheerzApiDraft } from "@Types/api/draft";
import { CheerzApiHeaderCategory } from "@Types/api/header";
import { CheerzApiLegal } from "@Types/api/legal";
import { CheerzApiPageResponse } from "@Types/api/pagify";
import {
  CheerzApiProcessOutCard,
  CheerzApiProcessOutInvoice,
  CheerzApiProcessOutTransaction,
  CheerzApiTransactionResponse,
} from "@Types/api/payment";
import { CheerzApiPromoCode } from "@Types/api/promoCode";
import { CheerzApiPromo } from "@Types/api/promos";
import {
  CheerzApiProviders,
  CheerzApiProvidersResponse,
  CheerzApiProvidersWithFotom,
  CheerzApiProviderToken,
} from "@Types/api/providers";
import { CheerzApiSettings } from "@Types/api/settings";
import { CheerzApiShippingOptions } from "@Types/api/shippingOption";
import CheerzApiUser, { CheerzApiUserChecked } from "@Types/api/user";
import { LoginProvider } from "@Types/login";

let CHEERZ_BACKEND_URL = process.env.REACT_APP_CHEERZ_BACKEND_URL;

if (!CHEERZ_BACKEND_URL) {
  throw new Error("Missing env REACT_APP_CHEERZ_BACKEND_URL");
}

if (CHEERZ_BACKEND_URL === "/") {
  CHEERZ_BACKEND_URL = window.location.origin;
}

export default class CheerzApiProvider {
  api: AxiosInstance;

  constructor(localeSlug: string, csrfToken: string, apiV2?: boolean) {
    const headers = {
      Accept: "application/json",
      "X-CSRF-TOKEN": csrfToken,
    };

    let baseURL = `${CHEERZ_BACKEND_URL || window.location.origin}/`;

    if (!apiV2) {
      baseURL += `${localeSlug}/`;
    }

    this.api = axios.create({
      baseURL,
      headers,
      withCredentials: true,
    });
  }

  /*
   * End points v1
   */

  async getCustomization(customizationId: string): Promise<CheerzApiCustomization> {
    const { data } = await this.api.get<CheerzApiCustomization>(
      `/customizations/${customizationId}/edit.json`
    );

    return data;
  }

  async getProviders(): Promise<CheerzApiProvidersWithFotom> {
    const { data, headers }: CheerzApiProvidersResponse = await this.api.get(`/providers.json`);

    const response: CheerzApiProvidersWithFotom = {
      fotomHost: window.location.host,
      fotomUserId: headers["cz-id"],
      fotomUserToken: headers["fotom-token"],
      providers: map(data, (datum) => {
        return {
          provider: datum.name,
          token: datum.token,
        };
      }),
    };

    return response;
  }

  async getProviderToken(
    prodiverTag: string,
    code: string | undefined
  ): Promise<CheerzApiProviderToken> {
    const params = {
      authorization_code: code,
      provider_name: prodiverTag,
    };
    const { data } = await this.api.post<CheerzApiProviderToken>(`/provider_token`, params);
    const response = {
      access_token: data.access_token,
      expires_in: data.expires_in,
    };
    return response;
  }

  async deleteProvider(prodiverTag: string, prodiverUid?: string): Promise<void> {
    const params: { name: string; logged: string } = {
      logged: prodiverUid ? prodiverUid : "",
      name: prodiverTag,
    };
    await this.api.delete<CheerzApiProviders[]>(`/provider.json`, { params });
  }

  async updateArticle(
    articleId: string,
    article: Partial<CheerzApiArticle>
  ): Promise<CheerzApiArticle> {
    const { data } = await this.api.put<CheerzApiArticle>(`/articles/${articleId}.json`, article);

    return data;
  }

  async addArticle(
    articleId: string,
    article: Partial<CheerzApiArticle> = {}
  ): Promise<CheerzApiArticle> {
    const { data } = await this.api.put<CheerzApiArticle>(
      `/articles/${articleId}/add.json`,
      article
    );

    return data;
  }

  async getCart(): Promise<CheerzApiCart> {
    const { data } = await this.api.get<CheerzApiCart>("/cart.json");

    return data;
  }

  async getCreditCard(): Promise<CheerzApiCreditCard | null> {
    const { data } = await this.api.get<CheerzApiCreditCard | null>("/credit_card.json");

    return data;
  }

  async getAddresses(): Promise<CheerzApiAddresses> {
    const { data } = await this.api.get<CheerzApiAddresses>("/order_address");

    return data;
  }

  async saveAddresses(addresses: Partial<CheerzApiAddresses>): Promise<CheerzApiAddresses> {
    const { data } = await this.api.put<CheerzApiAddresses>("/order_address", addresses);

    return data;
  }

  async getPromos(): Promise<CheerzApiPromo[]> {
    const { data } = await this.api.get<CheerzApiPromo[]>("/banner_groups/cart/index");

    return data;
  }

  async getUser(): Promise<CheerzApiUser> {
    const { data } = await this.api.get<CheerzApiUser>("/user_profile.json");

    return data;
  }

  async saveCart(cart: Partial<CheerzApiCart>): Promise<CheerzApiCart> {
    const { data } = await this.api.put<CheerzApiCart>("/cart.json", cart);

    return data;
  }

  async changeShippingOption({
    shipping_options,
  }: CheerzApiShippingOptions): Promise<CheerzApiCart> {
    const { data } = await this.api.put<CheerzApiCart>("/cart_shipping.json", {
      shipping_options,
    });

    return data;
  }

  async updatePromoCode(promoCode: CheerzApiPromoCode): Promise<CheerzApiCart> {
    const { data } = await this.api.put<CheerzApiCart>("/cart_promo.json", promoCode);

    return data;
  }

  async getBanners(): Promise<CheerzApiBannerKountries> {
    const { data } = await this.api.get<CheerzApiBannerKountries>(`/home_banners`);

    return data;
  }

  async getDrafts(): Promise<CheerzApiDraft[]> {
    const { data } = await this.api.get<CheerzApiDraft[]>(`/drafts`);

    return data;
  }

  async getCategories(): Promise<CheerzApiHeaderCategory[]> {
    const { data } = await this.api.get<CheerzApiHeaderCategory[]>(`/menu_items`);

    return data;
  }

  async checkPostCode(postcode: string, countryTag: string): Promise<boolean> {
    const { data } = await this.api.post<{ valid_zipcode: boolean }>(`/zipcode_checks`, {
      zipcode_checks: { zipcode: postcode, country_tag: countryTag },
    });

    return data.valid_zipcode;
  }

  async removeDraft(id: number): Promise<void> {
    await this.api.delete<CheerzApiDraft[]>(`/drafts/${id}`);
  }

  async findPaymentStatus(orderNumber: string): Promise<CheerzApiProvidersResponse> {
    const { data } = await this.api.get<CheerzApiProvidersResponse>(
      `/payment/status/${orderNumber}.json`
    );

    return data;
  }

  async createTransaction(
    transactionData: CheerzApiProcessOutTransaction
  ): Promise<CheerzApiTransactionResponse> {
    const { data } = await this.api.post<CheerzApiTransactionResponse>(
      `/payment/process_out_transactions.json`,
      transactionData
    );

    return data;
  }

  async createFreeTransaction(
    transactionData: CheerzApiProcessOutTransaction
  ): Promise<CheerzApiTransactionResponse> {
    const { data } = await this.api.post<CheerzApiTransactionResponse>(
      `/payment/free_transactions.json`,
      transactionData
    );

    return data;
  }

  async createProcessOutInvoice(
    transactionData: CheerzApiProcessOutTransaction
  ): Promise<CheerzApiProcessOutInvoice> {
    const { data } = await this.api.post<CheerzApiProcessOutInvoice>(
      `/payment/process_out_invoices`,
      transactionData
    );

    return data;
  }

  async saveProcessOutCard(
    transactionData: CheerzApiProcessOutTransaction
  ): Promise<CheerzApiProcessOutCard> {
    const { data } = await this.api.post<CheerzApiProcessOutCard>(
      `/payment/process_out_cards`,
      transactionData
    );

    return data;
  }

  async getProcessOutCard(orderNumber: string): Promise<CheerzApiProcessOutCard> {
    const { data } = await this.api.get<CheerzApiProcessOutCard>(
      `/payment/process_out_cards/${orderNumber}.json`
    );

    return data;
  }

  async payFree(orderNumber: string): Promise<CheerzApiTransactionResponse> {
    const { data } = await this.api.put<CheerzApiTransactionResponse>(
      `/payment/free/${orderNumber}.json`
    );

    return data;
  }

  async getPaymentStatus(orderNumber: string): Promise<CheerzApiTransactionResponse> {
    const { data } = await this.api.get<CheerzApiTransactionResponse>(
      `/payment/status/${orderNumber}.json`
    );

    return data;
  }

  async checkUser(params: {
    email?: string;
    name?: string;
    token?: string;
  }): Promise<CheerzApiUserChecked> {
    const { data } = await this.api.post<CheerzApiUserChecked>("/user_checks.json", {
      ...params,
    });

    return data;
  }

  async omniauthClient(
    email: string,
    token: string,
    provider: LoginProvider
  ): Promise<CheerzApiUser> {
    const { data } = await this.api.post<CheerzApiUser>("/omniauth_clients.json", {
      email,
      token,
      name: provider,
    });

    return data;
  }

  async sendRecoverPasswordEmail(email: string | undefined): Promise<CheerzApiUser> {
    const { data } = await this.api.post<CheerzApiUser>("/users/password.json", {
      email,
    });

    return data;
  }

  async signIn(email: string, password: string): Promise<CheerzApiUser> {
    const { data } = await this.api.post<CheerzApiUser>("/users/sign_in.json", {
      email,
      password,
    });

    return data;
  }

  async signOut(): Promise<void> {
    await this.api.delete<CheerzApiProviders[]>(`/users/sign_out.json`);
  }

  async signUp(
    firstName: string,
    email: string,
    password: string,
    unsubscribeNewsletter: boolean
  ): Promise<CheerzApiUser> {
    const [{ data }] = await Promise.all([
      await this.api.post<CheerzApiUser>("/users.json", {
        email,
        password,
        firstname: firstName,
        unsubscribe_newsletter: unsubscribeNewsletter,
      }),
      await this.api.post<{ agreement: true }>("/privacy_consents", { agreement: true }),
    ]);

    return data;
  }

  /*
   * End points v2
   */

  async getSettings(lang: string): Promise<CheerzApiSettings> {
    const { data } = await this.api.post<CheerzApiSettings>(
      `/api/v2/${lang}/settings?country_code3=nil`
    );

    return data;
  }

  async getLegal(settings: CheerzApiSettings["response"]): Promise<CheerzApiLegal> {
    const { country_code3, currency_code3, lang } = settings;
    const { data } = await this.api.get<CheerzApiLegal>(
      `/api/v2/public/${lang}/${country_code3}/${currency_code3}/dpo/legal_informations`
    );

    return data;
  }

  async getPrivacyPolicy(settings: CheerzApiSettings["response"]): Promise<CheerzApiLegal> {
    const { country_code3, currency_code3, lang } = settings;
    const { data } = await this.api.get<CheerzApiLegal>(
      `/api/v2/public/${lang}/${country_code3}/${currency_code3}/dpo/privacy_terms`
    );

    return data;
  }

  async getTermsAndConditions(settings: CheerzApiSettings["response"]): Promise<CheerzApiLegal> {
    const { country_code3, currency_code3, lang } = settings;
    const { data } = await this.api.get<CheerzApiLegal>(
      `/api/v2/public/${lang}/${country_code3}/${currency_code3}/dpo/terms_of_conditions`
    );

    return data;
  }

  async getHomePage(
    settings: CheerzApiSettings["response"]
  ): Promise<{ response: CheerzApiPageResponse }> {
    const { country_code3, currency_code3, lang } = settings;
    const { data } = await this.api.get<{ response: CheerzApiPageResponse }>(
      `/api/v2/public/${lang}/${country_code3}/${currency_code3}/web_pages/home`
    );

    return data;
  }

  async getPage(
    pageSlug: string,
    settings: CheerzApiSettings["response"]
  ): Promise<{ response: CheerzApiPageResponse }> {
    const { country_code3, currency_code3, lang } = settings;
    const { data } = await this.api.get<{ response: CheerzApiPageResponse }>(
      `/api/v2/public/${lang}/${country_code3}/${currency_code3}/web_pages/${pageSlug}`
    );

    return data;
  }
}
